import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';
import ScheduleTab from './components/ScheduleTab';
import TravelTab from './components/TravelTab';
import GuestlistTab from './components/GuestlistTab';
import CrewTab from './components/CrewTab';
import ToursTab from './components/ToursTab';

const TourShowDetail = () => {
  const { showId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('schedule');
  const [show, setShow] = useState(null);
  const [tourPlanId, setTourPlanId] = useState(null);

  // Set active tab from navigation state if provided
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const checkTourStatus = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/shows/tour-status', token);
      
      if (response.data.status === 200 && response.data.data.showTourMap) {
        const tourInfo = response.data.data.showTourMap[showId];
        setTourPlanId(tourInfo?.tourPlanId || null);
      }
    } catch (error) {
      console.error('Error checking tour status:', error);
    }
  };

  const loadShowDetails = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI(`user/tm/shows/${showId}`, token);
      
      if (response.data.status === 200 && response.data.data) {
        setShow(response.data.data);
        // Re-check tour status after show is loaded (in case tour was added)
        if (!tourPlanId) {
          checkTourStatus();
        }
      }
    } catch (error) {
      console.error('Error loading show details:', error);
    }
  };

  useEffect(() => {
    loadShowDetails();
    checkTourStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showId]);

  const tabs = [
    { id: 'schedule', label: 'Schedule' },
    { id: 'travel', label: 'Travel' },
    { id: 'guestlist', label: 'Guestlist' },
    { id: 'crew', label: 'Crew' },
    { id: 'tours', label: 'Tours' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (!show) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex items-center justify-center">
            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">Show not found</p>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-start">
            <div className="flex-1">
              <h1 className="text-white text-[14px] uppercase mb-1" style={{ fontWeight: 300 }}>
                {show.venue?.name || 'Unknown Venue'}
              </h1>
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-1">
                {show.venue?.city || 'Unknown City'}{show.venue?.state ? `, ${show.venue.state}` : ''}
              </p>
              <p className="text-[rgba(255,255,255,0.5)] text-[11px]">
                {formatDate(show.showDate)} · {show.showInfo?.showtimes || formatTime(show.showDate)}
              </p>
            </div>
            {tourPlanId ? (
              <button
                onClick={() => navigate(`/tour-manager/tour-overview/${tourPlanId}`, {
                  state: { fromShowDetail: true, showId }
                })}
                className="text-[#FF9500] text-[12px] font-normal cursor-pointer hover:opacity-70"
              >
                View Tour
              </button>
            ) : (
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Go back
              </span>
            )}
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FF9500]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="w-full flex-1 overflow-y-auto">
            {activeTab === 'schedule' && <ScheduleTab show={show} onRefresh={loadShowDetails} />}
            {activeTab === 'travel' && <TravelTab show={show} onRefresh={loadShowDetails} />}
            {activeTab === 'guestlist' && <GuestlistTab show={show} onRefresh={loadShowDetails} />}
            {activeTab === 'crew' && <CrewTab show={show} onRefresh={loadShowDetails} />}
            {activeTab === 'tours' && <ToursTab show={show} />}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default TourShowDetail;

